import { NavNode, NavEdge } from '../types/map';
import { OSMElement } from './overpass';

// Haversine distance between two coordinates in meters
export function haversineDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function classifyRoad(tags: Record<string, string>): NavEdge['classification'] {
  const highway = tags.highway;
  if (['motorway', 'trunk', 'primary', 'secondary'].includes(highway)) return 'major';
  if (['tertiary'].includes(highway)) return 'minor';
  if (['residential', 'unclassified', 'living_street', 'service'].includes(highway)) return 'residential';
  return 'footpath';
}

function signalWeightForClassification(classification: NavEdge['classification']): number {
  switch (classification) {
    case 'major': return 0.7;
    case 'minor': return 0.45;
    case 'residential': return 0.25;
    case 'footpath': return 0.1;
  }
}

// Calculate the "straightness" of a geometry — ratio of direct distance to path distance
function isStraightGeometry(geometry: { lat: number; lon: number }[]): boolean {
  if (geometry.length < 2) return true;
  const first = geometry[0];
  const last = geometry[geometry.length - 1];
  const directDist = haversineDistance(first.lat, first.lon, last.lat, last.lon);
  let pathDist = 0;
  for (let i = 1; i < geometry.length; i++) {
    pathDist += haversineDistance(
      geometry[i - 1].lat, geometry[i - 1].lon,
      geometry[i].lat, geometry[i].lon
    );
  }
  if (pathDist === 0) return true;
  return directDist / pathDist > 0.95; // >95% straight
}

export function buildNavigationGraph(
  streets: OSMElement[]
): { nodes: NavNode[]; edges: NavEdge[] } {
  const nodeMap = new Map<string, NavNode>();
  const edges: NavEdge[] = [];

  // Round coordinates to ~1m precision to merge nearby intersections
  const coordKey = (lat: number, lng: number) =>
    `${lat.toFixed(5)},${lng.toFixed(5)}`;

  const getOrCreateNode = (lat: number, lng: number): NavNode => {
    const key = coordKey(lat, lng);
    if (!nodeMap.has(key)) {
      nodeMap.set(key, {
        id: key,
        lat,
        lng,
        edges: [],
      });
    }
    return nodeMap.get(key)!;
  };

  // Count how many ways reference each coordinate to find intersections
  const coordUsageCount = new Map<string, number>();
  for (const way of streets) {
    if (!way.geometry || way.geometry.length < 2) continue;
    for (const point of way.geometry) {
      const key = coordKey(point.lat, point.lon);
      coordUsageCount.set(key, (coordUsageCount.get(key) || 0) + 1);
    }
    // Endpoints are always nodes
    const firstKey = coordKey(way.geometry[0].lat, way.geometry[0].lon);
    coordUsageCount.set(firstKey, (coordUsageCount.get(firstKey) || 0) + 1);
    const lastKey = coordKey(
      way.geometry[way.geometry.length - 1].lat,
      way.geometry[way.geometry.length - 1].lon
    );
    coordUsageCount.set(lastKey, (coordUsageCount.get(lastKey) || 0) + 1);
  }

  for (const way of streets) {
    if (!way.geometry || way.geometry.length < 2) continue;
    const tags = way.tags || {};
    const classification = classifyRoad(tags);
    const isOneWay = tags.oneway === 'yes' || tags.oneway === '-1';
    const isBridge = tags.bridge === 'yes';
    const isTunnel = tags.tunnel === 'yes';
    const wayName: string = (tags.name || '').trim();

    // Split way at intersections (coords used by >1 way)
    let segmentStart = 0;
    for (let i = 1; i < way.geometry.length; i++) {
      const point = way.geometry[i];
      const key = coordKey(point.lat, point.lon);
      const isIntersection = (coordUsageCount.get(key) || 0) > 1;
      const isEndpoint = i === way.geometry.length - 1;

      if (isIntersection || isEndpoint) {
        const segGeometry = way.geometry.slice(segmentStart, i + 1);
        if (segGeometry.length < 2) {
          segmentStart = i;
          continue;
        }

        const fromPoint = segGeometry[0];
        const toPoint = segGeometry[segGeometry.length - 1];
        const fromNode = getOrCreateNode(fromPoint.lat, fromPoint.lon);
        const toNode = getOrCreateNode(toPoint.lat, toPoint.lon);

        if (fromNode.id === toNode.id) {
          segmentStart = i;
          continue;
        }

        // Calculate edge length
        let length = 0;
        for (let j = 1; j < segGeometry.length; j++) {
          length += haversineDistance(
            segGeometry[j - 1].lat, segGeometry[j - 1].lon,
            segGeometry[j].lat, segGeometry[j].lon
          );
        }

        const edgeId = `${way.id}-${segmentStart}-${i}`;
        const geom: [number, number][] = segGeometry.map(p => [p.lon, p.lat]);

        const edge: NavEdge = {
          id: edgeId,
          from: fromNode.id,
          to: toNode.id,
          length,
          classification,
          signalWeight: signalWeightForClassification(classification),
          zone: 'residential', // default, overridden by zone classifier
          isStraight: isStraightGeometry(segGeometry),
          isOneWay,
          isBridge,
          isTunnel,
          geometry: geom,
          name: wayName || undefined,
        };

        edges.push(edge);
        fromNode.edges.push(edgeId);
        toNode.edges.push(edgeId);

        segmentStart = i;
      }
    }
  }

  // --- Implicit connections: link nearby disconnected footpath endpoints to road nodes ---
  // This allows the player to transition between pedestrian paths and roads
  // even when OSM doesn't have an explicit connection.
  const MAX_IMPLICIT_DIST = 60; // meters — max gap to bridge
  const allNodes = Array.from(nodeMap.values());

  // Build a set of which edges are footpath-classified
  const footpathEdgeIds = new Set<string>();
  for (const edge of edges) {
    if (edge.classification === 'footpath') {
      footpathEdgeIds.add(edge.id);
    }
  }

  // Find footpath nodes that have few connections (dead-ends or only footpath connections)
  const roadNodes: NavNode[] = [];
  const footpathEndpoints: NavNode[] = [];

  for (const node of allNodes) {
    const hasRoadEdge = node.edges.some(eid => !footpathEdgeIds.has(eid));
    if (hasRoadEdge) {
      roadNodes.push(node);
    }
    // Node is a footpath-only node if ALL its edges are footpaths — include dead ends,
    // simple midpoints, AND junctions so park path networks can connect to roads
    const allFootpath = node.edges.every(eid => footpathEdgeIds.has(eid));
    if (allFootpath && node.edges.length > 0) {
      footpathEndpoints.push(node);
    }
  }

  // For each footpath endpoint, find the nearest road node within range and create an implicit edge
  let implicitCount = 0;
  for (const fpNode of footpathEndpoints) {
    let nearestRoad: NavNode | null = null;
    let nearestDist = Infinity;

    for (const rNode of roadNodes) {
      const d = haversineDistance(fpNode.lat, fpNode.lng, rNode.lat, rNode.lng);
      if (d < nearestDist && d <= MAX_IMPLICIT_DIST) {
        nearestDist = d;
        nearestRoad = rNode;
      }
    }

    if (nearestRoad) {
      // Check if they're already connected
      const alreadyConnected = fpNode.edges.some(eid => {
        const e = edges.find(ed => ed.id === eid);
        return e && (e.from === nearestRoad!.id || e.to === nearestRoad!.id);
      });
      if (alreadyConnected) continue;

      const implicitId = `implicit-${implicitCount++}`;
      const implicitEdge: NavEdge = {
        id: implicitId,
        from: fpNode.id,
        to: nearestRoad.id,
        length: nearestDist,
        classification: 'footpath',
        signalWeight: 0.1,
        zone: 'residential',
        isStraight: true,
        isOneWay: false,
        isBridge: false,
        isTunnel: false,
        geometry: [
          [fpNode.lng, fpNode.lat],
          [nearestRoad.lng, nearestRoad.lat],
        ],
      };
      edges.push(implicitEdge);
      fpNode.edges.push(implicitId);
      nearestRoad.edges.push(implicitId);
    }
  }

  assignRoadIds(edges);

  return {
    nodes: allNodes,
    edges,
  };
}

/**
 * Assign a stable `roadId` to every named edge in the graph. Two edges are
 * in the same "road" iff they share a name AND are reachable from each
 * other through a chain of same-name edges that share nodes. Disconnected
 * same-name segments (two "Main Street" stretches in different
 * neighborhoods) get different roadIds.
 *
 * Mutates the edges in place — writes `roadId` on every edge with a name.
 * Edges without a name are skipped and their roadId stays undefined.
 *
 * Exported so runtime city loaders can backfill roadId on cached city data
 * whose NavEdges predate this field.
 */
export function assignRoadIds(edges: NavEdge[]): void {
  // Build adjacency: for each node id, the set of (edge index, name) touching it.
  const nodeToEdges = new Map<string, number[]>();
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i];
    if (!e.name) continue;
    const fromList = nodeToEdges.get(e.from) ?? [];
    fromList.push(i);
    nodeToEdges.set(e.from, fromList);
    const toList = nodeToEdges.get(e.to) ?? [];
    toList.push(i);
    nodeToEdges.set(e.to, toList);
  }

  const visited = new Set<number>();
  let nextRoadId = 0;
  for (let i = 0; i < edges.length; i++) {
    if (visited.has(i)) continue;
    const seed = edges[i];
    if (!seed.name) continue;
    const roadId = `road-${nextRoadId++}`;
    // BFS over edges reachable through same-name connections.
    const queue: number[] = [i];
    visited.add(i);
    while (queue.length > 0) {
      const idx = queue.shift()!;
      const edge = edges[idx];
      edge.roadId = roadId;
      for (const nodeId of [edge.from, edge.to]) {
        const neighbors = nodeToEdges.get(nodeId);
        if (!neighbors) continue;
        for (const nIdx of neighbors) {
          if (visited.has(nIdx)) continue;
          if (edges[nIdx].name === seed.name) {
            visited.add(nIdx);
            queue.push(nIdx);
          }
        }
      }
    }
  }
}
